import { Box } from '@mui/material';
import { RadarChartProps, radarClasses } from '@mui/x-charts';
import React, { useMemo } from 'react';

import ChartLegend from 'components/charts/chart-legend';
import RadarChart from 'components/charts/radar-chart';
import { SERIES_HIGHLIGHT } from 'components/charts/constants/chart-defaults';
import { useChartLegendVisibility } from 'components/charts/hooks/use-chart-legend-visibility';
import { useChartAnimation } from 'components/charts/hooks/use-chart-animation';
import { useLegendHighlight } from 'components/charts/hooks/use-legend-highlight';
import { useSeriesVisibility } from 'components/charts/hooks/use-series-visibility';
import { useChartPalette } from 'components/charts/hooks/use-chart-palette';
import DataLabChartHiddenSeriesOverlay from 'views/playground/features/data-lab/components/data-lab-chart-hidden-series-overlay';
import DataLabChartLegendVisibilityMenu from 'views/playground/features/data-lab/components/data-lab-chart-legend-visibility-menu';
import { NamedSeries } from 'views/playground/features/data-lab/types/chart-series.type';

type Props = Pick<RadarChartProps, 'height'> & {
  data: { metrics: string[]; series: NamedSeries[] };
};

// Not selectable: two overlaid condition profiles compared side by side, not
// individually clickable domain entities.
const ConditionRadarChart: React.FC<Props> = ({ data, height = 320 }) => {
  const palette = useChartPalette();
  const chartSkipAnimation = useChartAnimation();
  const { visible: legendVisible, toggle: toggleLegend } = useChartLegendVisibility();

  const coloredSeries = useMemo(
    () =>
      data.series.map((series, index) => ({
        ...series,
        color: palette.series.categorical[index % palette.series.categorical.length],
      })),
    [data.series, palette.series.categorical]
  );

  const { hiddenIds, toggle, visibleSeries } = useSeriesVisibility(coloredSeries);
  const { highlightedItem, onHighlightChange, onLegendItemHover, onLegendItemLeave } = useLegendHighlight();
  // The card cannot see per-series visibility, so this chart owns the
  // all-series-hidden message.
  const allSeriesHidden = coloredSeries.length > 0 && visibleSeries.length === 0;

  return (
    <>
      <Box display="flex" justifyContent="flex-end">
        <DataLabChartLegendVisibilityMenu visible={legendVisible} onToggle={toggleLegend} />
      </Box>
      <RadarChart
        height={height}
        skipAnimation={chartSkipAnimation}
        hideLegend
        radar={{ metrics: data.metrics, max: 100 }}
        series={visibleSeries.map((series) => ({
          ...series,
          data: series.data.map((value) => value ?? 0),
          fillArea: true,
          ...SERIES_HIGHLIGHT,
        }))}
        highlightedItem={highlightedItem}
        onHighlightChange={onHighlightChange}
        slots={{ noDataOverlay: DataLabChartHiddenSeriesOverlay }}
        sx={{
          [`& .${radarClasses.seriesArea}`]: { fillOpacity: 0.25 },
          // Dims the radar's own scale/metric text so the frame reads as inert
          // rather than real data; state-dependent, so it cannot live in the
          // theme.
          ...(allSeriesHidden && { '& text:not(.chart-hidden-series-overlay-text)': { opacity: 0.4 } }),
        }}
      />
      {legendVisible && (
        <ChartLegend
          items={coloredSeries.map((series) => ({ id: String(series.id), label: series.label, color: series.color }))}
          hiddenIds={hiddenIds}
          onToggle={toggle}
          onItemHover={onLegendItemHover}
          onItemLeave={onLegendItemLeave}
        />
      )}
    </>
  );
};

export default ConditionRadarChart;
